import { FC, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { MiControlledCheckBox, MiDetailFields } from "@miplatform/components";
import { INPUT_TYPES } from "@miplatform/constants";
import { AreaDesignation, ICategoriesResponse } from "@miplatform/types";
import { TransferList } from "@mibuildweb/components";
import { Box } from "@mui/material";
import { ConfigAccordionHeader } from "../../../../../Config/components/ConfigAccordionHeader/ConfigAccordionHeader";
import { IAreaTypeData } from "../../AreaTypes.types";

export const AreaTypeDetailsTab: FC<AreaTypeDetailsTabProps> = ({
  form,
  extraTabProps,
}) => {
  const { categories = [], isReadOnly = false } = extraTabProps || {};
  const { control, setValue, watch } = form;

  const currentCategories = watch("categories") || [];

  const sortedCategories = useMemo(
    () =>
      [...currentCategories].sort((a, b) => a.displayOrder - b.displayOrder),
    [currentCategories]
  );

  const areaDesignationOptions = useMemo(
    () =>
      Object.values(AreaDesignation).map((value) => ({
        label: `${value}`.replaceAll(/([A-Z])/g, " $1").trim(),
        value,
      })),
    []
  );

  const areaTypeFields = [
    {
      label: "Area Name",
      name: "name",
      required: true,
      formControl: control,
      disabled: isReadOnly,
      placeholder: "Enter area name",
      inputProps: { maxLength: 128 },
    },
    {
      label: "Area Type",
      name: "areaDesignation",
      fieldType: INPUT_TYPES.SELECT,
      formControl: control,
      disabled: isReadOnly,
      required: true,
      options: areaDesignationOptions,
    },
    {
      fieldType: INPUT_TYPES.RADIO_GROUP,
      name: "specColumn",
      setValue: (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue("specColumn", Number.parseInt(e.target.value, 10), {
          shouldValidate: true,
          shouldDirty: true,
        });
      },
      formControl: control,
      label: "Spec Location Designation",
      helperText: "Select which side of the bid this area should appear",
      disabled: isReadOnly,
      required: true,
      options: [
        { label: "Left Side", value: 0 },
        { label: "Right Side", value: 1 },
      ],
      width: "100%",
      row: true,
      sx: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
      },
    },
  ];

  const availableCategories = categories
    .filter((cat) => !currentCategories.some((c) => c.categoryId === cat.id))
    .map((cat) => ({
      key: cat.id,
      title: cat.name,
    }));

  const selectedCategories = sortedCategories.map((cat) => ({
    key: cat.categoryId,
    title: cat.categoryName,
  }));

  const handleAddCategory = (key: string) => {
    const category = categories.find((c) => c.id === key);
    if (!category) return;
    const updatedCategories = [
      ...currentCategories,
      {
        categoryId: category.id,
        categoryName: category.name,
        isRequired: true,
        displayOrder: currentCategories.length,
      },
    ];
    setValue("categories", updatedCategories, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleRemoveCategory = (key: string) => {
    const updatedCategories = sortedCategories
      .filter((cat) => cat.categoryId !== key)
      .map((cat, index) => ({ ...cat, displayOrder: index }));

    setValue("categories", updatedCategories, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const customAction = (_: unknown, index: number) => {
    const category = sortedCategories[index];
    if (!category) return null;

    return (
      <Box onClick={(e) => e.stopPropagation()}>
        <MiControlledCheckBox
          formControl={control}
          label="Optional"
          disabled={isReadOnly}
          value={!category.isRequired}
          handleChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const isRequired = !e.target.checked;
            const updatedCategories = currentCategories.map((cat) =>
              cat.categoryId === category.categoryId
                ? { ...cat, isRequired }
                : cat
            );
            setValue("categories", updatedCategories, {
              shouldValidate: true,
              shouldDirty: true,
            });
          }}
        />
      </Box>
    );
  };

  return (
    <>
      <ConfigAccordionHeader title="Area Details">
        <MiDetailFields
          detailFields={areaTypeFields}
          headerProps={{
            subtitle: "Enter the area name and spec location designation.",
            variant: "text",
          }}
        />
      </ConfigAccordionHeader>
      <ConfigAccordionHeader title="Available Categories">
        <TransferList
          availableItems={availableCategories}
          selectedItems={selectedCategories}
          onAddItem={handleAddCategory}
          onRemoveItem={handleRemoveCategory}
          availableTitle="Available Categories"
          selectedTitle="Selected Categories"
          customAction={customAction}
          disabled={isReadOnly}
          showAvailableItemsSearch
        />
      </ConfigAccordionHeader>
    </>
  );
};

interface AreaTypeDetailsTabProps {
  form: UseFormReturn<IAreaTypeData>;
  extraTabProps?: {
    categories?: ICategoriesResponse[];
    isReadOnly?: boolean;
  };
}
